import { useQuery } from '@tanstack/react-query';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';
import type { DiaAgenda, ItemAgenda as ItemAgendaTramitacao } from '@/components/kanban/tramitacao/AgendaTramitacao';

/* O SLA DE CADA PEDIDO (Douglas 24/10/2026): a Minha fila, a Fila da equipe e
   a coluna Prazo das triagens leem a mesma régua do banco (sla_filas +
   fn_sla_fila), a mesma que a trava usa. Minutos são de expediente. */

export type FilaMinha = {
  fila: string; rotulo: string; prazo: string; triagem: string;
  total: number; estourados: number; em_1h: number; em_30: number;
  primeiro_min: number | null;
  vencem_30: { pedido_id: string; numero_pedido: number | null; restam_min: number }[];
};

export type MinhaFila = {
  pessoa: { id: string; nome: string; funcao: string | null };
  expediente: boolean;
  em_almoco: boolean;
  travada: { fila: string; rotulo: string; travado_em: string; minutos: number } | null;
  totais: { estourados: number; em_1h: number; no_prazo: number };
  filas: FilaMinha[];
};

export type FilaDaEquipe = {
  expediente: boolean;
  totais: { estourados: number; em_1h: number; no_prazo: number };
  pessoas: MinhaFila[];
};

export type FeitoPessoa = {
  feitos: number; media: number | null; tratou: number;
  primeiro: string | null; ultimo: string | null;
  detalhe: { rotulo: string; pedidos: number }[];
};

/** Os horários do dia da BKO Tramitação (botão ao lado de Feitos hoje). */
export function useAgendaTramitacao(pessoa: string | null, ativo = true) {
  return useQuery({
    queryKey: ['sla', 'agenda-tramitacao', pessoa ?? 'eu'],
    enabled: ativo,
    staleTime: 20_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<{ aplica: boolean; agenda: ItemAgendaTramitacao[] }> => {
      const { data, error } = await rpcTriagem('fn_bko_tramitacao_agenda', pessoa ? { p_pessoa: pessoa } : {});
      if (error) throw new Error(error.message || 'Não deu para carregar os horários.');
      return data as { aplica: boolean; agenda: ItemAgendaTramitacao[] };
    },
  });
}

/** Os horários das travas do BKO Contrato: as travas reais do dia e a próxima checagem. */
export function useAgendaContrato(pessoa: string | null, ativo = true) {
  return useQuery({
    queryKey: ['sla', 'agenda-contrato', pessoa ?? 'eu'],
    enabled: ativo,
    staleTime: 20_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<{ aplica: boolean; agenda: ItemAgendaTramitacao[] } & DiaAgenda> => {
      const { data, error } = await rpcTriagem('fn_bko_contrato_agenda', pessoa ? { p_pessoa: pessoa } : {});
      if (error) throw new Error(error.message || 'Não deu para carregar os horários.');
      return data as { aplica: boolean; agenda: ItemAgendaTramitacao[] } & DiaAgenda;
    },
  });
}

export function useAgendaPortabilidade(pessoa: string | null, ativo = true) {
  return useQuery({
    queryKey: ['sla', 'agenda-portabilidade', pessoa ?? 'eu'],
    enabled: ativo,
    staleTime: 20_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<{ aplica: boolean; agenda: ItemAgendaTramitacao[] } & DiaAgenda> => {
      const { data, error } = await rpcTriagem('fn_bko_portabilidade_agenda', pessoa ? { p_pessoa: pessoa } : {});
      if (error) throw new Error(error.message || 'Não deu para carregar os horários.');
      return data as { aplica: boolean; agenda: ItemAgendaTramitacao[] } & DiaAgenda;
    },
  });
}

export type FeitoHoje = { dia: string; feitos: number; tratou: number; pessoas: Record<string, FeitoPessoa> };

export type PrazoPedido = { restam_min: number; estourado: boolean; obs: string | null; prazo: string };

export function useMinhaFila(pessoa: string | null, ativo = true) {
  return useQuery({
    queryKey: ['sla', 'minha-fila', pessoa ?? 'eu'],
    enabled: ativo,
    staleTime: 20_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<MinhaFila> => {
      const { data, error } = await rpcTriagem('fn_minha_fila', pessoa ? { p_pessoa: pessoa } : {});
      if (error) throw new Error(error.message || 'Não deu para carregar a fila.');
      return data as MinhaFila;
    },
  });
}

export function useFilaDaEquipe(ativo = true) {
  return useQuery({
    queryKey: ['sla', 'fila-da-equipe'],
    enabled: ativo,
    staleTime: 20_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<FilaDaEquipe> => {
      const { data, error } = await rpcTriagem('fn_fila_da_equipe', {});
      if (error) throw new Error(error.message || 'Não deu para carregar a fila da equipe.');
      return data as FilaDaEquipe;
    },
  });
}

/** O feito do dia por pessoa (Douglas 24/10/2026): pedidos que saíram da fila pela mão dela, a média e a tratativa. */
export function useFeitoHoje(ativo = true) {
  return useQuery({
    queryKey: ['sla', 'feito-hoje'],
    enabled: ativo,
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<FeitoHoje> => {
      const { data, error } = await rpcTriagem('fn_feito_hoje', {});
      if (error) throw new Error(error.message || 'Não deu para carregar o feito do dia.');
      return data as FeitoHoje;
    },
  });
}

export function useSlaPrazos(coluna: string | null | undefined) {
  return useQuery({
    queryKey: ['sla', 'prazos', coluna],
    enabled: !!coluna,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Record<string, PrazoPedido>> => {
      const { data, error } = await rpcTriagem('fn_sla_prazos', { p_coluna: coluna });
      if (error) throw new Error(error.message || 'Não deu para carregar os prazos.');
      return (data ?? {}) as Record<string, PrazoPedido>;
    },
  });
}

/** O painel do Backoffice: o prazo de cada pedido por coluna (estourado ≤ 0). */
export function useSlaPainel() {
  return useQuery({
    queryKey: ['sla', 'painel'],
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Record<string, Record<string, number>>> => {
      const { data, error } = await rpcTriagem('fn_sla_painel', {});
      if (error) throw new Error(error.message || 'Não deu para carregar os prazos do painel.');
      return (data ?? {}) as Record<string, Record<string, number>>;
    },
  });
}

/** "vence em 35 min", "vence em 2 h 59", "atrasado há 3 dias e 4 h" (minutos de expediente:
    o dia útil tem 10 h e o fim de semana não conta, Douglas 30/10/2026). */
export function rotuloPrazo(restamMin: number | null | undefined): string {
  if (restamMin == null) return '-';
  const m = Math.abs(Math.round(restamMin));
  const DIA = 600;
  const dias = Math.floor(m / DIA);
  const horas = Math.floor((m % DIA) / 60);
  const txt = m < 60 ? `${m} min`
    : dias === 0 ? `${horas} h${m % 60 ? ` ${String(m % 60).padStart(2, '0')}` : ''}`
    : `${dias} ${dias === 1 ? 'dia' : 'dias'}${horas ? ` e ${horas} h` : ''}`;
  return restamMin <= 0 ? `atrasado há ${txt}` : `vence em ${txt}`;
}
